import {FilterModel,
        FilterJSON,
        FilterFactory} from './modelsFilter';
//************************************************//
//************************************************//
//************************************************//

export class PlotlyChartLayout {

  title?: any;
  xaxis?: Record<string, any>;
  yaxis?: Record<string, any>;
  annotations?: any[];
  dragmode?: string;
  legend?: Record<string, any>;
  margin?: Record<string, any>;
  paper_bgcolor?: string;
  plot_bgcolor?: string;
  showlegend?: boolean;
  autosize?: boolean;
  height?: number;
  width?: number;
  template?: string;
  font?: Record<string, any>;

  constructor(
    title?: any,
    xaxis?: Record<string, any>,
    yaxis?: Record<string, any>,
    annotations?: any[],
    dragmode?: string,
    legend?: Record<string, any>,
    margin?: Record<string, any>,
    paper_bgcolor?: string,
    plot_bgcolor?: string,
    showlegend?: boolean,
    autosize?: boolean,
    height?: number,
    width?: number,
    template?: string,
    font?: Record<string, any>
  ) {

    this.title=title
    this.xaxis=xaxis
    this.yaxis=yaxis
    this.annotations=annotations
    this.dragmode=dragmode
    this.legend=legend
    this.margin=margin
    this.paper_bgcolor=paper_bgcolor
    this.plot_bgcolor=plot_bgcolor
    this.showlegend=showlegend
    this.autosize=autosize
    this.height=height
    this.width=width
    this.template=template
    this.font=font
  }

  fromJSON(json: Partial<PlotlyChartLayout>): PlotlyChartLayout {
    return new PlotlyChartLayout(
      json.title ?? '',
      json.xaxis ?? {title: ''},
      json.yaxis ?? {title: ''},
      json.annotations ?? [],
      json.dragmode,
      json.legend,
      json.margin ?? {l: 60, r:40, t: 40, b:60},
      json.paper_bgcolor,
      json.plot_bgcolor,
      json.showlegend ?? true,
      json.autosize ?? true,
      json.height,
      json.width,
      json.template,
      json.font
    );
  }


}

export class PlotlyChartData {
  type?: string;
  mode?: string;
  name?: string;
  x?: any[];
  y?: any[];
  showlegend?: boolean;
  text?: any;
  marker?: Record<string, any>;
  line?: Record<string, any>;
  hoverinfo?: string;
  hovertemplate?: string;
  opacity?: number;
  customdata?: any[];
  labels?: any[]; //required for Pie Charts
  values?: any[]; //required for Pie Charts
  value?: number; //required for Gauge Charts
  gauge?: any; //required for Gauge Charts
  delta?: any; //required for Gauge Charts
  domain?: any; //required for Gauge Charts
  header?: any; //required for plotly Table
  cells?: any;
  ids?: string[]

  constructor(
    type?: string,
    mode?: string,
    name?: string,
    x?: any[],
    y?: any[],
    showlegend?: boolean,
    text?: any,
    marker?: Record<string, any>,
    line?: Record<string, any>,
    hoverinfo?: string,
    hovertemplate?: string,
    opacity?: number,
    customdata?: any[],
    labels?: any[],
    values?: any[],
    value?: number,
    gauge?: any,
    delta?: any,
    domain?: any,
    header?: any,
    cells?: any,
    ids?: string[]
  ) {

    this.type = type;
    this.mode = mode;
    this.name = name;
    this.x = x;
    this.y = y;
    this.showlegend = showlegend
    this.text = text;
    this.marker = marker;
    this.line = line;
    this.hoverinfo = hoverinfo;
    this.hovertemplate = hovertemplate;
    this.opacity = opacity;
    this.customdata = customdata;
    this.labels = labels;
    this.values = values;
    this.value = value;
    this.gauge = gauge;
    this.delta = delta;
    this.domain = domain;
    this.header = header;
    this.cells = cells;
    this.ids = ids;


  }

  fromJSON(json: Partial<PlotlyChartData>): PlotlyChartData {
    return new PlotlyChartData(
      json.type ?? 'scatter',
      json.mode,
      json.name,
      json.x ?? [],
      json.y ?? [],
      json.showlegend,
      json.text,
      json.marker ?? {color: 'blue', size: 6, symbol: 'circle'},
      json.line,
      json.hoverinfo,
      json.hovertemplate,
      json.opacity,
      json.customdata,
      json.labels,
      json.values,
      json.value,
      json.gauge,
      json.delta,
      json.domain,
      json.header,
      json.cells,
      json.ids
    );
  }

}


export class PlotlyChartFigure {

  data: PlotlyChartData[];
  layout: PlotlyChartLayout;
  config: any;

  constructor(
    data: PlotlyChartData[] = [],
    layout: PlotlyChartLayout = new PlotlyChartLayout(),
    config: any = {}
  ) {

    this.data = data;
    this.layout = layout;
    this.config = config;
  }

  fromJSON(json: any): PlotlyChartFigure {
    const data = (json.data || []).map((thisData: any) => new PlotlyChartData().fromJSON(thisData))
    const layout = new PlotlyChartLayout().fromJSON(json.layout)
    const config = json.config ?? {};

    const thisFigure = new PlotlyChartFigure(data, layout, config)

    return thisFigure

  }
}

//************************************************//
//************************************************//
//************************************************//

export type KPICardVariantType = 'stat' | 'trend' | 'progress';

export interface KPICardText {
  text?: string,
  color?: string,
  size?: string,
  weight?: string
}

export interface KPICardRow {
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around'
  elements?: KPICardText[]
}

export interface KPICardIcon {
  name?: string,
  type?: string,
  color?: string,
  background_color?: string,
}

export interface KPICardTrend {
  direction?: string,
  value?: string,
  label?: string,
  color?: string
}

export interface KPICardSparkline {
  data?: number[],
  color?: string
}

export class KPICardData {
  variant?: KPICardVariantType;
  title?: KPICardText;
  subtitle?: KPICardText;
  body?: KPICardText;
  icon?: KPICardIcon;
  trend?: KPICardTrend;
  sparkline?: KPICardSparkline;
  rows?: KPICardRow[];

  constructor(
    variant?: KPICardVariantType,
    title?: KPICardText,
    subtitle?: KPICardText,
    body?: KPICardText,
    icon?: KPICardIcon,
    trend?: KPICardTrend,
    sparkline?: KPICardSparkline,
    rows?: KPICardRow[]
  ) {
    this.variant = variant;
    this.title = title;
    this.subtitle = subtitle;
    this.body = body;
    this.icon = icon;
    this.trend = trend;
    this.sparkline = sparkline;
    this.rows = rows
  }

  fromJSON(json: Partial<KPICardData>): KPICardData {
    return new KPICardData(
      json.variant,
      json.title ?? {},
      json.subtitle ?? {},
      json.body ?? {},
      json.icon ?? {},
      json.trend ?? {},
      json.sparkline ?? {},
      json.rows ?? []
    );
  }
}


export class InfoCardData {
  title?: KPICardText;
  subtitle?: KPICardText;
  icon?: KPICardIcon;
  rows?: KPICardRow[];

  constructor(
    title?: KPICardText,
    subtitle?: KPICardText,
    icon?: KPICardIcon,
    rows?: KPICardRow[]
  ) {
    this.title = title;
    this.subtitle = subtitle;
    this.icon = icon;
    this.rows = rows
  }

  fromJSON(json: Partial<InfoCardData>): InfoCardData {
    return new InfoCardData(
      json.title ?? {},
      json.subtitle ?? {},
      json.icon ?? {},
      json.rows ?? []
    );
  }
}



export class KPICardFigure {

  data: KPICardData;

  constructor(
    data: KPICardData
  ) {
    this.data = data;
  }

  fromJSON(json: KPICardData): KPICardFigure {
    const data = new KPICardData().fromJSON(json)
    return new KPICardFigure(data)
  }
}

export class InfoCardFigure {

  data: InfoCardData;

  constructor(
    data: InfoCardData
  ) {
    this.data = data;
  }

  fromJSON(json: InfoCardData): InfoCardFigure {
    const data = new InfoCardData().fromJSON(json)
    return new InfoCardFigure(data)
  }
}

//************************************************//
//************************************************//
//************************************************//

export type WidgetType = 'PlotlyChart' | 'KPICard' | 'InfoCard'

export class ChartWidget {

  id: string;
  type: WidgetType;
  width: number;
  figure: PlotlyChartFigure | KPICardFigure;

  constructor(
    id: string,
    type: WidgetType,
    width: number,
    figure: PlotlyChartFigure | KPICardFigure
  ) {
    this.id = id;
    this.type = type;
    this.width = width;
    this.figure = figure;
  }
}

export class ChartWidgetFactory {
  fromJSON(json: any): ChartWidget {
    switch(json.type as WidgetType){
      case 'PlotlyChart':
         const dataPlotly = (json.figure.data || []).map((thisData: any) => new PlotlyChartData().fromJSON(thisData))
         const layoutPlotly = new PlotlyChartLayout().fromJSON(json.figure.layout)
         const configPlotly = json.figure.config ?? {};
         const thisPlotlyFigure = new PlotlyChartFigure(dataPlotly, layoutPlotly, configPlotly);
         return new ChartWidget(json.id, 'PlotlyChart', 12, thisPlotlyFigure)
      case 'KPICard':
         const dataCard = new KPICardData().fromJSON(json.figure)
         const thisKPICard = new KPICardFigure(dataCard);
         return new ChartWidget(json.id, 'KPICard', 4, thisKPICard)
      case 'InfoCard':
         const infoCard = new InfoCardData().fromJSON(json.figure)
         const thisInfoCard = new InfoCardFigure(infoCard);
         return new ChartWidget(json.id, 'InfoCard', 4, thisInfoCard)
      default:
        throw new Error(`Unsupported component type: ${JSON.stringify(json)}`)
    }
  }
}


//************************************************//
//************************************************//
//************************************************//

export interface ScreenElementChartJSON {
  Id_sys: string,
  Uuid_sys: string,
  Label_c: string,
  widgets: any[],
  filters: FilterJSON[]
}

export class ScreenElementChartModel {
  Id_sys: string;
  Uuid_sys: string;
  Label_c: string;
  widgets: ChartWidget[];
  filters: FilterModel[];
  constructor(
    Id_sys: string,
    Uuid_sys: string,
    Label_c: string,
    widgets: ChartWidget[],
    filters: FilterModel[]
  ) {
    this.Id_sys = Id_sys;
    this.Uuid_sys = Uuid_sys;
    this.Label_c = Label_c;
    this.widgets = widgets;
    this.filters = filters;
  }
}

export class ScreenElementChartFactory {
  fromJSON(json: ScreenElementChartJSON): ScreenElementChartModel {

    const filterFactory = new FilterFactory();
    const thisFilters = json.filters.map((thisFilter: FilterJSON) => filterFactory.fromJSON(thisFilter))

    //note: cannot use widget factory, because
    //a lot of config would otherwise bet set to undefined
    //and cannot be used for plotly
    //const widgetFactory = new ChartWidgetFactory();
    //const thisWidgets = json.widgets.map((thisWidget: any) => widgetFactory.fromJSON(thisWidget))
    const thisWidgets = json.widgets.map((thisWidget: any) => thisWidget);

    return new ScreenElementChartModel(
      json.Id_sys,
      json.Uuid_sys,
      json.Label_c,
      thisWidgets,
      thisFilters
    )
  }
}

/*
NO NEED OF COMPLEX <T extends> structure
export type WidgetType = 'PlotlyChart' | 'KPICard'
export type WidgetTypeMap = {
  PlotlyChart: PlotlyChartFigure,
  KPICard: KPICardFigure
}

export class ChartWidget<T extends WidgetType=WidgetType> {

  id: string;
  type: T;
  figure: WidgetTypeMap[T];

  constructor(
    id: string,
    type: T,
    figure: WidgetTypeMap[T]
  ) {
    this.id = id;
    this.type = type;
    this.figure = figure;
  }
}

export class ChartWidgetFactory {
  fromJSON(json: any): ChartWidget<WidgetType> {
    switch(json.type as WidgetType){
      case 'PlotlyChart':
         const dataPlotly = (json.figure.data || []).map((thisData: any) => new PlotlyChartData().fromJSON(thisData))
         const layoutPlotly = new PlotlyChartLayout().fromJSON(json.figure.layout)
         const configPlotly = json.figure.config ?? {};
         const thisPlotlyFigure = new PlotlyChartFigure(dataPlotly, layoutPlotly, configPlotly);
         return new ChartWidget(json.id, 'PlotlyChart', thisPlotlyFigure)
      case 'KPICard':
         const dataCard = new KPICardData().fromJSON(json.figure)
         const thisKPICard = new KPICardFigure(dataCard);
         return new ChartWidget(json.id, 'KPICard', thisKPICard)
      default:
        throw new Error(`Unsupported component type: ${JSON.stringify(json)}`)
    }
  }
}
*/
